import React, { lazy, Suspense } from "react";
import { LazyMotion, domAnimation, AnimatePresence } from "framer-motion";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Loader from "../components/Loader";
import ScrollToTop from "./ScrollToTop";

// Clients
const ClientLogin = lazy(() => import("../pages/Clients/ClientLogin"));
const ClientDashBoard = lazy(() => import("../pages/Clients/ClientDashboard"));
const ClientSub = lazy(() => import("../pages/Clients/ClientSub"));

// Client Daily Entries
const ClientDailyEntries = lazy(() =>
  import("../pages/Clients/ClientDailyEntries")
);
const ClientGRVReport = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/GRVReport")
);
const ClientPurchaseReport = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/PurchaseReport")
);
const ClientPurchaseReturnReport = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/PurchaseReturnReport")
);
const ClientPurchaseFromOtherBranches = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/PurchaseFromOtherBranchesReport")
);
const ClientPurchaseInvoiceScan = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/PurchaseInvoiceScan")
);
const ClientAttendanceReport = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/AttendanceReport")
);
const ClientSalesReport = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/SalesReport")
);
const ClientSalesQuantativeSummary = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/SalesQuantativeSummary")
);
const ClientInventoryStockLedger = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/InventoryStockLedger")
);
const ClientItemExpiry = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/ItemExpiry")
);
const ClientClosingStock = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/ClosingStock")
);
const ClientCashBankStatement = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/CashBankStatement")
);
const ClientMerchantSummary = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/MerchantSummary")
);
const ClientOutstandingStatement = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/OutstandingStatement")
);
const ClientSalesToOtherBranch = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/SalesToOtherBranch")
);
const ClientPaymentVoucherScan = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/PaymentVoucherScan")
);
const ClientOredooOmantel = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/OredooOmantel")
);
const ClientBankStatement = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/BankStatement")
);
const ClientCreditSales = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/CreditSales")
);
const ClientProductCostingMan = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/ProductCostingMan")
);
const ClientProductCostingServices = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/ProductCostingServices")
);
const ClientMonthlyDiscount = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/MonthlyDiscount")
);
const ClientMonthlyOffers = lazy(() =>
  import("../pages/Clients/ClientDailyEntries/MonthlyOffers")
);
const ClientPurchases = lazy(() => import("../pages/Clients/ClientPurchases"));
const ClientSales = lazy(() => import("../pages/Clients/ClientSales"));
const ClientInvoices = lazy(() => import("../pages/Clients/ClientInvoice"));
const ClientPRInvoices = lazy(() => import("../pages/Clients/ClientPRInvoice"));
const ClientApplicants = lazy(() =>
  import("../pages/Clients/ClientApplicants")
);
const ClientApplicantDetailsCreate = lazy(() =>
  import("../pages/Clients/ClientApplicantCreation/ApplicantDetails")
);
const ClientCompanyCreate = lazy(() =>
  import("../pages/Clients/ClientApplicantCreation/CompanyDetails")
);
const ClientRiyadaCreate = lazy(() =>
  import("../pages/Clients/ClientApplicantCreation/RiyadaDetails")
);
const ClientProjectCreate = lazy(() =>
  import("../pages/Clients/ClientApplicantCreation/ProjectDetails")
);
const ClientLandCreate = lazy(() =>
  import("../pages/Clients/ClientApplicantCreation/LandDetails")
);
const ClientPECreate = lazy(() =>
  import("../pages/Clients/ClientApplicantCreation/PropertyEvaluation")
);
const ClientBankStatementCreate = lazy(() =>
  import("../pages/Clients/ClientApplicantCreation/BankStatements")
);
const PreClientReports = lazy(() =>
  import("../pages/Clients/PreClientReports")
);
const ClientNotes = lazy(() =>
  import("../pages/Clients/PreClientReports/Notes")
);
const ClientLogs = lazy(() => import("../pages/Clients/PreClientReports/Logs"));

//  Accounting Financial Statements
const ClientFinancialStatements = lazy(() =>
  import("../pages/Clients/ClientFinancialStatements")
);
const ClientBalanceSheet = lazy(() =>
  import("../pages/Clients/ClientFinancialStatements/BalanceSheet")
);
const ClientIncomeStatement = lazy(() =>
  import("../pages/Clients/ClientFinancialStatements/IncomeStatement")
);
const ClientVATReport = lazy(() =>
  import("../pages/Clients/ClientFinancialStatements/VATReport")
);
const ClientVATReturn = lazy(() =>
  import("../pages/Clients/ClientFinancialStatements/VATReturn")
);
const ClientTaxReport = lazy(() =>
  import("../pages/Clients/ClientFinancialStatements/TaxReport")
);
const ClientVATReturnTracker = lazy(() =>
  import("../pages/Clients/ClientFinancialStatements/VATReturnTracker")
);

// const ClientRegister = lazy(() => import("../pages/Clients/ClientRegister"));

// Accounting
const AccountingLogin = lazy(() =>
  import("../pages/Accounting/AccountingLogin")
);
const AccountingDashboard = lazy(() =>
  import("../pages/Accounting/AccountingDashBoard")
);
const ManageAccountant = lazy(() =>
  import("../pages/Accounting/ManageAccountant")
);
const AccountingClients = lazy(() =>
  import("../pages/Accounting/AccountingClients")
);
const AccountingClientInformation = lazy(() =>
  import("../pages/Accounting/AccountingClientInformation")
);
const AccountingClientAttachments = lazy(() =>
  import("../pages/Accounting/AccountingClientAttachments")
);
const AccountingClientAccess = lazy(() =>
  import("../pages/Accounting/AccountingClientAccess")
);
const PreAccountData = lazy(() => import("../pages/Accounting/PreAccountData"));
const AccountingData = lazy(() => import("../pages/Accounting/AccountingData"));
const Report = lazy(() => import("../pages/Accounting/Report"));
const ReportsFormat = lazy(() => import("../pages/Accounting/ReportsFormat"));
const PreManageClient = lazy(() =>
  import("../pages/Accounting/PreManageClient")
);
const PreManageAccountant = lazy(() =>
  import("../pages/Accounting/PreManageAccountant")
);
// Accounting Entries - Accountant
const Entries = lazy(() => import("../pages/Accounting/DailyEntries"));
const GRVReport = lazy(() =>
  import("../pages/Accounting/DailyEntries/GRVReport")
);
const PurchaseReport = lazy(() =>
  import("../pages/Accounting/DailyEntries/PurchaseReport")
);
const PurchaseReturnReport = lazy(() =>
  import("../pages/Accounting/DailyEntries/PurchaseReturnReport")
);
const PurchaseFromOtherBranches = lazy(() =>
  import("../pages/Accounting/DailyEntries/PurchaseFromOtherBranchesReport")
);
const PurchaseInvoiceScan = lazy(() =>
  import("../pages/Accounting/DailyEntries/PurchaseInvoiceScan")
);
const AttendanceReport = lazy(() =>
  import("../pages/Accounting/DailyEntries/AttendanceReport")
);
const SalesReport = lazy(() =>
  import("../pages/Accounting/DailyEntries/SalesReport")
);
const SalesQuantativeSummary = lazy(() =>
  import("../pages/Accounting/DailyEntries/SalesQuantativeSummary")
);
const InventoryStockLedger = lazy(() =>
  import("../pages/Accounting/DailyEntries/InventoryStockLedger")
);
const ItemExpiry = lazy(() =>
  import("../pages/Accounting/DailyEntries/ItemExpiry")
);
const ClosingStock = lazy(() =>
  import("../pages/Accounting/DailyEntries/ClosingStock")
);
const CashBankStatement = lazy(() =>
  import("../pages/Accounting/DailyEntries/CashBankStatement")
);
const MerchantSummary = lazy(() =>
  import("../pages/Accounting/DailyEntries/MerchantSummary")
);
const OutstandingStatement = lazy(() =>
  import("../pages/Accounting/DailyEntries/OutstandingStatement")
);
const SalesToOtherBranch = lazy(() =>
  import("../pages/Accounting/DailyEntries/SalesToOtherBranch")
);
const PaymentVoucherScan = lazy(() =>
  import("../pages/Accounting/DailyEntries/PaymentVoucherScan")
);
const OredooOmantel = lazy(() =>
  import("../pages/Accounting/DailyEntries/OredooOmantel")
);
const BankStatement = lazy(() =>
  import("../pages/Accounting/DailyEntries/BankStatement")
);
const CreditSales = lazy(() =>
  import("../pages/Accounting/DailyEntries/CreditSales")
);
const ProductCostingMan = lazy(() =>
  import("../pages/Accounting/DailyEntries/ProductCostingMan")
);
const ProductCostingServices = lazy(() =>
  import("../pages/Accounting/DailyEntries/ProductCostingServices")
);
const MonthlyDiscount = lazy(() =>
  import("../pages/Accounting/DailyEntries/MonthlyDiscount")
);
const MonthlyOffers = lazy(() =>
  import("../pages/Accounting/DailyEntries/MonthlyOffers")
);
const Purchases = lazy(() => import("../pages/Accounting/Purchases"));
const Sales = lazy(() => import("../pages/Accounting/Sales"));
//  Accounting Financial Statements
const FinancialStatements = lazy(() =>
  import("../pages/Accounting/FinancialStatements")
);
const BalanceSheet = lazy(() =>
  import("../pages/Accounting/FinancialStatements/BalanceSheet")
);
const IncomeStatement = lazy(() =>
  import("../pages/Accounting/FinancialStatements/IncomeStatement")
);
const VATReport = lazy(() =>
  import("../pages/Accounting/FinancialStatements/VATReport")
);
const VATReturn = lazy(() =>
  import("../pages/Accounting/FinancialStatements/VATReturn")
);
const TaxReport = lazy(() =>
  import("../pages/Accounting/FinancialStatements/TaxReport")
);
const VATReturnTracker = lazy(() =>
  import("../pages/Accounting/FinancialStatements/VATReturnTracker")
);
const Invoices = lazy(() => import("../pages/Accounting/Invoice"));
const PRInvoices = lazy(() => import("../pages/Accounting/PRInvoice"));
const AccountantAccess = lazy(() =>
  import("../pages/Accounting/AccountantAccess")
);
const Log = lazy(() => import("../pages/Accounting/Log"));
const Applicants = lazy(() => import("../pages/Accounting/Applicants"));
const ApplicantDetailsCreate = lazy(() =>
  import("../pages/Accounting/ApplicantCreation/ApplicantDetails")
);
const RiyadaCreate = lazy(() =>
  import("../pages/Accounting/ApplicantCreation/RiyadaDetails")
);
const CompanyCreate = lazy(() =>
  import("../pages/Accounting/ApplicantCreation/CompanyDetails")
);
const ProjectCreate = lazy(() =>
  import("../pages/Accounting/ApplicantCreation/ProjectDetails")
);
const LandCreate = lazy(() =>
  import("../pages/Accounting/ApplicantCreation/LandDetails")
);
const PECreate = lazy(() =>
  import("../pages/Accounting/ApplicantCreation/PropertyEvaluation")
);
const BankStatementCreate = lazy(() =>
  import("../pages/Accounting/ApplicantCreation/BankStatements")
);
const ApplicantProfile = lazy(() =>
  import("../pages/Accounting/Applicants/applicantProfile")
);
const ClientApplicantProfile = lazy(() =>
  import("../pages/Clients/ClientApplicants/applicantProfile")
);

// Misc
const PageNotFound = lazy(() => import("../components/NoPageFound"));
const NotAuthorize = lazy(() => import("../components/NotAuthorize"));

const Routing = ({ language, changeLanguage }) => {
  return (
    <div>
      <Suspense fallback={<Loader minHeight={"80vh"} />}>
        <Router>
          <AnimatePresence>
            <LazyMotion features={domAnimation}>
              <ScrollToTop />
              <Routes>
                {/* Clients */}
                <Route
                  path="/client"
                  element={
                    <ClientLogin
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                {/* <Route
                  path="/client/register"
                  element={<ClientRegister />}
                ></Route> */}
                {/* <Route
                    path="/client/contacts"
                    element={<ClientContacts />}
                  ></Route> */}
                <Route
                  path="/client/dashboard"
                  element={
                    <ClientDashBoard
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/subClient/:id/:name"
                  element={
                    <ClientSub
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                {/* Client Daily Entries */}
                <Route
                  path="/accounting/client/dailyentries/:id/:name"
                  element={
                    <ClientDailyEntries
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/applicants"
                  element={
                    <ClientApplicants
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/applicantProfile/:id/:clientId"
                  element={
                    <ClientApplicantProfile
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/applicantDetails/:type"
                  element={
                    <ClientApplicantDetailsCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/applicantDetails/:type/:id"
                  element={
                    <ClientApplicantDetailsCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/companyDetails/:clientId/:id"
                  element={
                    <ClientCompanyCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/riyadaDetails/:clientId/:id"
                  element={
                    <ClientRiyadaCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/projectDetails/:clientId/:id"
                  element={
                    <ClientProjectCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/landDetails/:clientId/:id"
                  element={
                    <ClientLandCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/propertyEvaluation/:clientId/:id"
                  element={
                    <ClientPECreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/client/bankStatement/:clientId/:id"
                  element={
                    <ClientBankStatementCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/grvReport/:id/:name"
                  element={
                    <ClientGRVReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/purchaseReport/:id/:name"
                  element={
                    <ClientPurchaseReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/purchaseReturnReport/:id/:name"
                  element={
                    <ClientPurchaseReturnReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/purchaseFromOtherBranchesReport/:id/:name"
                  element={
                    <ClientPurchaseFromOtherBranches
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/purchaseInvoiceScan/:id/:name"
                  element={
                    <ClientPurchaseInvoiceScan
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/attendanceReport/:id/:name"
                  element={
                    <ClientAttendanceReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/salesReport/:id/:name"
                  element={
                    <ClientSalesReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/salesQSummary/:id/:name"
                  element={
                    <ClientSalesQuantativeSummary
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/inventoryStockLedger/:id/:name"
                  element={
                    <ClientInventoryStockLedger
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/itemExpiry/:id/:name"
                  element={
                    <ClientItemExpiry
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/closingStock/:id/:name"
                  element={
                    <ClientClosingStock
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/cashBankStatement/:id/:name"
                  element={
                    <ClientCashBankStatement
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/merchantSummary/:id/:name"
                  element={
                    <ClientMerchantSummary
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/outstandingStatement/:id/:name"
                  element={
                    <ClientOutstandingStatement
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/salesToOtherBranch/:id/:name"
                  element={
                    <ClientSalesToOtherBranch
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/paymentVoucherScan/:id/:name"
                  element={
                    <ClientPaymentVoucherScan
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/ooPayment/:id/:name"
                  element={
                    <ClientOredooOmantel
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/bs/:id/:name"
                  element={
                    <ClientBankStatement
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/creditSales/:id/:name"
                  element={
                    <ClientCreditSales
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/pcm/:id/:name"
                  element={
                    <ClientProductCostingMan
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/pcs/:id/:name"
                  element={
                    <ClientProductCostingServices
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/monthlyDiscount/:id/:name"
                  element={
                    <ClientMonthlyDiscount
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/entries/monthlyOffers/:id/:name"
                  element={
                    <ClientMonthlyOffers
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/dailentries/purchases/:id/:name"
                  element={
                    <ClientPurchases
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/dailentries/sales/:id/:name"
                  element={
                    <ClientSales
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/fs/:id/:name"
                  element={
                    <ClientFinancialStatements
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/fs/balancesheet/:id/:name"
                  element={
                    <ClientBalanceSheet
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/fs/incomestatement/:id/:name"
                  element={
                    <ClientIncomeStatement
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/fs/vatreport/:id/:name"
                  element={
                    <ClientVATReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/fs/vatreturn/:id/:name"
                  element={
                    <ClientVATReturn
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/fs/taxreport/:id/:name"
                  element={
                    <ClientTaxReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/fs/vatreturntracker/:id/:name"
                  element={
                    <ClientVATReturnTracker
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/invoice/:id/:name"
                  element={
                    <ClientInvoices
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/prinvoice/:id/:name"
                  element={
                    <ClientPRInvoices
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/preReports"
                  element={
                    <PreClientReports
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/preReports/notes"
                  element={
                    <ClientNotes
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/client/preReports/logs"
                  element={
                    <ClientLogs
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                {/* Accounting - Accountant & AH */}
                <Route
                  path="/accounting"
                  element={
                    <AccountingLogin
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/dashboard"
                  element={
                    <AccountingDashboard
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/manageAccountant"
                  element={
                    <ManageAccountant
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/preManageClient"
                  element={
                    <PreManageClient
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/preManageAccountant"
                  element={
                    <PreManageAccountant
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/manageClientAccess"
                  element={
                    <AccountingClientAccess
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/clients"
                  element={
                    <AccountingClients
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/clientInformation"
                  element={
                    <AccountingClientInformation
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/clientAttachments"
                  element={
                    <AccountingClientAttachments
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/preselectiondata"
                  element={
                    <PreAccountData
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/data"
                  element={
                    <AccountingData
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/accaccess"
                  element={
                    <AccountantAccess
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/:type/report/:id/:name"
                  element={
                    <Report
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/reportsFormat"
                  element={
                    <ReportsFormat
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/applicants"
                  element={
                    <Applicants
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/applicantDetails/:type"
                  element={
                    <ApplicantDetailsCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/applicantDetails/:type/:id"
                  element={
                    <ApplicantDetailsCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/companyDetails/:clientId/:id"
                  element={
                    <CompanyCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/riyadaDetails/:clientId/:id"
                  element={
                    <RiyadaCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/projectDetails/:clientId/:id"
                  element={
                    <ProjectCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/landDetails/:clientId/:id"
                  element={
                    <LandCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/propertyEvaluation/:clientId/:id"
                  element={
                    <PECreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/bankStatement/:clientId/:id"
                  element={
                    <BankStatementCreate
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/applicantProfile/:id/:clientId"
                  element={
                    <ApplicantProfile
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/log"
                  element={
                    <Log language={language} changeLanguage={changeLanguage} />
                  }
                ></Route>
                {/* Accounting Entries */}
                <Route
                  path="/accounting/entries/:id/:name"
                  element={
                    <Entries
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/grvReport/:id/:name"
                  element={
                    <GRVReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/purchaseReport/:id/:name"
                  element={
                    <PurchaseReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/purchaseReturnReport/:id/:name"
                  element={
                    <PurchaseReturnReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/purchaseFromOtherBranchesReport/:id/:name"
                  element={
                    <PurchaseFromOtherBranches
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/purchaseInvoiceScan/:id/:name"
                  element={
                    <PurchaseInvoiceScan
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/attendanceReport/:id/:name"
                  element={
                    <AttendanceReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/salesReport/:id/:name"
                  element={
                    <SalesReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/salesQSummary/:id/:name"
                  element={
                    <SalesQuantativeSummary
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/inventoryStockLedger/:id/:name"
                  element={
                    <InventoryStockLedger
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/itemExpiry/:id/:name"
                  element={
                    <ItemExpiry
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/closingStock/:id/:name"
                  element={
                    <ClosingStock
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/cashBankStatement/:id/:name"
                  element={
                    <CashBankStatement
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/merchantSummary/:id/:name"
                  element={
                    <MerchantSummary
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/outstandingStatement/:id/:name"
                  element={
                    <OutstandingStatement
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/salesToOtherBranch/:id/:name"
                  element={
                    <SalesToOtherBranch
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/paymentVoucherScan/:id/:name"
                  element={
                    <PaymentVoucherScan
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/ooPayment/:id/:name"
                  element={
                    <OredooOmantel
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/bs/:id/:name"
                  element={
                    <BankStatement
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/creditSales/:id/:name"
                  element={
                    <CreditSales
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/pcm/:id/:name"
                  element={
                    <ProductCostingMan
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/pcs/:id/:name"
                  element={
                    <ProductCostingServices
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/monthlyDiscount/:id/:name"
                  element={
                    <MonthlyDiscount
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/entries/monthlyOffers/:id/:name"
                  element={
                    <MonthlyOffers
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/fs"
                  element={
                    <FinancialStatements
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/fs/balancesheet"
                  element={
                    <BalanceSheet
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/fs/incomestatement"
                  element={
                    <IncomeStatement
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/fs/vatreport"
                  element={
                    <VATReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/fs/vatreturn"
                  element={
                    <VATReturn
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/fs/taxreport"
                  element={
                    <TaxReport
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/fs/vatreturntracker"
                  element={
                    <VATReturnTracker
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/invoice/:id/:name"
                  element={
                    <Invoices
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/prinvoice/:id/:name"
                  element={
                    <PRInvoices
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/dailentries/purchases/:id/:name"
                  element={
                    <Purchases
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                <Route
                  path="/accounting/dailentries/sales/:id/:name"
                  element={
                    <Sales
                      language={language}
                      changeLanguage={changeLanguage}
                    />
                  }
                ></Route>
                {/* Misc */}
                <Route path="/notAuthorized" element={<NotAuthorize />}></Route>
                <Route path="*" element={<PageNotFound />}></Route>
              </Routes>
            </LazyMotion>
          </AnimatePresence>
        </Router>
      </Suspense>
    </div>
  );
};

export default Routing;
